import type { CollabAnnotation } from '@/services/collab-session-service';
import { getAnnotationPosition } from './collabTypes';

export function captureTextSelection(container: HTMLElement | null): {
  selectedText: string;
  start: number;
  end: number;
} | null {
  if (!container) return null;
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || sel.rangeCount === 0) return null;

  const range = sel.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) return null;

  const selectedText = sel.toString().trim();
  if (!selectedText) return null;

  const preRange = document.createRange();
  preRange.selectNodeContents(container);
  preRange.setEnd(range.startContainer, range.startOffset);
  const start = preRange.toString().length;
  const end = start + range.toString().length;

  return { selectedText, start, end };
}

export interface AnnotatedTextSegment {
  text: string;
  annotation?: CollabAnnotation;
}

export function buildAnnotatedSegments(
  body: string,
  annotations: CollabAnnotation[],
): AnnotatedTextSegment[] {
  const ranges = annotations
    .filter((ann) => {
      if (ann.isResolved) return false;
      const pos = getAnnotationPosition(ann);
      return typeof pos.start === 'number' && typeof pos.end === 'number' && pos.end > pos.start;
    })
    .map((ann) => {
      const pos = getAnnotationPosition(ann);
      return { start: pos.start as number, end: pos.end as number, ann };
    })
    .sort((a, b) => a.start - b.start);

  if (ranges.length === 0) {
    return [{ text: body }];
  }

  const segments: AnnotatedTextSegment[] = [];
  let cursor = 0;

  for (const { start, end, ann } of ranges) {
    const safeStart = Math.max(0, Math.min(start, body.length));
    const safeEnd = Math.max(safeStart, Math.min(end, body.length));
    if (safeStart > cursor) {
      segments.push({ text: body.slice(cursor, safeStart) });
    }
    if (safeEnd > safeStart) {
      segments.push({ text: body.slice(safeStart, safeEnd), annotation: ann });
    }
    cursor = Math.max(cursor, safeEnd);
  }

  if (cursor < body.length) {
    segments.push({ text: body.slice(cursor) });
  }

  return segments;
}
